// Real, researched articles (2026-07-15) — full bodies with verified references
// live in src/content/articles/<slug>.html, rendered by pages/ArticleDetail.tsx.
// Publishing pipeline is still WP-CLI + a bundled copy here, not a live REST
// fetch at build time — see original-savage/Original Savage Heartbeat.md for
// the fuller automation plan once the heartbeat starts running regularly.
export interface VotePoll {
  key: string
  question: string
  options: string[]
  baseSplit: number[]
  startingVotes: number
}

export interface Article {
  slug: string
  tag: string
  date: string
  title: string
  dek: string
  /**
   * Short SERP meta description (<=155 chars). `dek` is the on-page standfirst and
   * runs long by design; Ahrefs Site Audit flagged every article for "meta description
   * too long" on 2026-09-01, so the head tags use this when present and fall back to `dek`.
   */
  metaDescription?: string
  authorSlug: string
  vote?: VotePoll
}

export const featuredArticle: Article = {
  slug: 'how-long-does-stored-rice-actually-last',
  tag: 'Food & Water Storage',
  date: 'Jul 14 2026',
  title: 'How Long Does Stored Rice Actually Last?',
  dek: 'Properly sealed white rice genuinely stores for 25–30 years, but brown rice goes rancid in months. Here is what actually decides shelf life, which storage methods hold up, and where the marketing claims are real versus optimistic.',
  metaDescription: 'Sealed white rice stores 25 to 30 years, brown rice months. What sets shelf life, how storage methods compare, and where the claims get optimistic.',
  authorSlug: 'trond-jacobsen',
  vote: {
    key: 'rice',
    question: 'How do you store your long-term rice?',
    options: ['Mylar + oxygen absorbers', 'Food-grade buckets, sealed', 'Vacuum bags in the freezer', 'Canning', 'Freeze drying'],
    baseSplit: [49, 27, 11, 8, 5],
    startingVotes: 3812,
  },
}

export const articles: Article[] = [
  {
    slug: 'how-long-do-batteries-last',
    tag: 'Home & Power',
    date: 'Oct 26',
    title: 'How Long Do Batteries Last?',
    dek: 'About 10 years in storage for alkaline AAs, 20 to 25 for lithium, and three to five years of service for a car battery, which can still die in a month if the car sits. Shelf life by chemistry, how to store lithium-ion so it does not age, and a battery plan for an emergency kit.',
    metaDescription: 'Alkaline AAs last about 10 years in storage, lithium 20 to 25, car batteries 3 to 5 years. Shelf life by chemistry and how to store each one.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'batteries',
      question: 'What powers the lights in your emergency kit?',
      options: ['Lithium AAs', 'Alkaline AAs', 'NiMH rechargeables', 'Power bank or power station', 'Honestly, not sure'],
      baseSplit: [31, 29, 14, 15, 11],
      startingVotes: 1964,
    },
  },
  {
    slug: 'how-long-does-it-take-to-season-firewood',
    tag: 'Skills',
    date: 'Oct 26',
    title: 'How Long Does It Take to Season Firewood?',
    dek: 'Six months at minimum and a full year to be safe, until the wood reads under 20 percent moisture. How to split and stack it so it dries faster, the signs that it is ready, and the two-minute moisture meter test that settles it.',
    metaDescription: 'Firewood takes 6 to 12 months to season, a year to be safe, until it reads under 20% moisture. How to dry it faster and tell when it is ready.',
    authorSlug: 'arthur-banks',
    vote: {
      key: 'firewood',
      question: 'How far ahead is your firewood?',
      options: ['Two winters or more', 'One winter ahead', 'Cutting this year for this year', 'I buy it seasoned', 'No wood heat'],
      baseSplit: [18, 34, 14, 21, 13],
      startingVotes: 1587,
    },
  },
  {
    slug: 'how-long-do-mres-last',
    tag: 'Food & Water',
    date: 'Oct 22',
    title: 'How Long Do MREs Last?',
    dek: 'The official figure is three years at 80°F. The famous 130-month chart is 1980s data on older rations. Why heat decides almost everything, how to read the time-temperature indicator and date code, and when an old pouch goes in the trash.',
    metaDescription: 'MREs last 3 years at 80°F per DLA, longer if cool, months if hot. The old 130-month chart, date codes, the TTI circle, and when to toss a pouch.',
    authorSlug: 'trond-jacobsen',
  },
  {
    slug: 'how-long-do-seeds-last',
    tag: 'Food & Water',
    date: 'Oct 22',
    title: 'How Long Do Seeds Last?',
    dek: 'Most vegetable seeds stay viable two to five years kept cool and dry, onions and parsnips about one, leeks about six. A crop-by-crop longevity table, how to store seed so it beats the chart, and the ten-seed test that tells you whether old packets are still worth sowing.',
    metaDescription: 'Most vegetable seeds last 2 to 5 years cool and dry; onion and parsnip about 1. Longevity by crop, long-term storage, and the 10-seed germination test.',
    authorSlug: 'gordon-nassaur',
  },
  {
    slug: 'how-long-can-a-generator-run-continuously',
    tag: 'Home & Power',
    date: 'Oct 14',
    title: 'How Long Can a Generator Run Continuously?',
    dek: 'The number on the box is one tank of fuel. The real ceiling is oil: a check at every refuel, a change every 100 hours on a portable, and a 30 minute rest every 24 hours on a standby unit. How to keep one running through a ten-day outage instead of a weekend.',
    metaDescription: 'A generator runs as long as you fuel it and do the oil. Tank runtimes, 100 hour oil changes, the 24 hour standby rest, and multi-day outage planning.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'genruntime',
      question: 'How do you run your generator in a long outage?',
      options: ['Around the clock, refuel as needed', 'In blocks, a few hours at a time', 'Only for the fridge and freezer', 'Standby unit, it just runs', 'No generator yet'],
      baseSplit: [18, 41, 17, 9, 15],
      startingVotes: 1204,
    },
  },
  {
    slug: 'how-long-does-salt-last',
    tag: 'Food & Water',
    date: 'Oct 14',
    title: 'How Long Does Salt Last?',
    dek: 'Pure salt never spoils. The date on the canister is about the additives: iodine that fades over about five years, and anti-caking agents that eventually give up. Why iodized salt is the one worth rotating when you eat from storage, and how to keep the rest dry forever.',
    metaDescription: 'Pure salt lasts forever. Iodized salt should be rotated in about 5 years as iodine fades. Shelf life by salt type, storage, and when to replace it.',
    authorSlug: 'gordon-nassaur',
  },
  {
    slug: 'how-long-does-powdered-milk-last',
    tag: 'Food & Water',
    date: 'Oct 06',
    title: 'How Long Does Powdered Milk Last?',
    dek: 'Three to five years cool, under a year in a hot garage, and the twenty-five year figure on the bucket is answering a different question. The research numbers by temperature, why nonfat is the only version that stores, and what changes the moment you add water.',
    metaDescription: 'Research puts nonfat dry milk at 3 to 5 years cool and under a year warm. Why nonfat only, what the 25 year claim means, and mixing it safely.',
    authorSlug: 'trond-jacobsen',
  },
  {
    slug: 'how-long-does-flour-last',
    tag: 'Food & Water',
    date: 'Sep 22',
    title: 'How Long Does Flour Last?',
    dek: 'About a year for white flour, one to three months for whole wheat, and the difference is the oil in the germ. What the date on the bag actually means, why the bugs arrived with it, and why a deep pantry stores wheat rather than flour.',
    metaDescription: 'White flour keeps about a year, whole wheat 1 to 3 months. Why the germ oil decides it, what the date means, and how to store flour for years.',
    authorSlug: 'gordon-nassaur',
  },
  {
    slug: 'how-to-pack-a-backpack',
    tag: 'Gear & Reviews',
    date: 'Sep 22',
    title: 'How to Pack a Backpack',
    dek: 'Two people can carry the same gear at the same weight and have completely different days. Where the weight sits decides whether your hips carry the load or your back fights it all afternoon.',
    metaDescription: 'Bulky and light at the bottom, heavy and dense against your back, essentials on top. The three-zone system, the 20 percent rule, and loading order.',
    authorSlug: 'arthur-banks',
    vote: {
      key: 'packing',
      question: 'Where does the heavy gear sit in your pack?',
      options: ['Core, against my back', 'Down at the bottom', 'Wherever it fits', 'Split across both sides', 'I have never thought about it'],
      baseSplit: [29, 19, 27, 11, 14],
      startingVotes: 1724,
    },
  },
  {
    slug: 'how-long-does-kerosene-last',
    tag: 'Home & Power',
    date: 'Sep 16',
    title: 'How Long Does Kerosene Last?',
    dek: 'Two to five years sealed, about a year once opened, and no standards body will give you an official number. What actually ages it, why the grade matters more than the date, and the blue-can rule that keeps gasoline out of your heater.',
    metaDescription: 'Clear 1-K kerosene keeps 2 to 5 years sealed, about a year opened. Why grade beats age, what water and sunlight do, and how to tell if it has gone bad.',
    authorSlug: 'trond-jacobsen',
    vote: {
      key: 'kerosene',
      question: 'Do you keep kerosene on hand for heat or light?',
      options: ['Yes, for a heater', 'Yes, for lamps only', 'Used to, gave it up', 'Never have', 'Propane instead'],
      baseSplit: [31, 12, 14, 29, 14],
      startingVotes: 1438,
    },
  },
  {
    slug: 'how-to-read-a-topographic-map',
    tag: 'Skills',
    date: 'Sep 16',
    title: 'How to Read a Topographic Map',
    dek: 'Every brown line is a walk you could take without going up or down. Learn the contour interval, the index lines, which way the Vs point, and how spacing reads as steepness, and any USGS quad becomes a route you can plan.',
    metaDescription: 'Contour lines, intervals, index contours, the V rule, scale and declination. How to read a USGS topographic map and turn it into a route you can walk.',
    authorSlug: 'arthur-banks',
    vote: {
      key: 'topo',
      question: 'How do you navigate off the road?',
      options: ['Paper map and compass', 'GPS app, paper backup', 'GPS app only', 'I know the ground', 'I stay on the trail'],
      baseSplit: [22, 34, 21, 9, 14],
      startingVotes: 1876,
    },
  },
  {
    slug: 'how-long-does-diesel-fuel-last',
    tag: 'Home & Power',
    date: 'Sep 10',
    title: 'How Long Does Diesel Fuel Last?',
    dek: 'Six to twelve months untreated, three to five years done properly, and the difference is almost entirely water. Diesel does not go stale the way gasoline does. It grows something at the bottom of the tank, and by the time you notice, the filters are already gone.',
    metaDescription: 'Untreated diesel keeps 6 to 12 months, treated and dry 3 to 5 years. Why water decides it, what diesel bug is, and the biodiesel nobody labels.',
    authorSlug: 'trond-jacobsen',
    vote: {
      key: 'diesel',
      question: 'How do you keep stored fuel usable?',
      options: ['Stabilizer and biocide at fill', 'Rotate into a vehicle twice a year', 'Small cans, buy fresh, no storage', 'Propane instead, it does not age', 'Honestly, I do not rotate it'],
      baseSplit: [23, 31, 17, 14, 15],
      startingVotes: 2107,
    },
  },
  {
    slug: 'what-size-inverter-do-i-need',
    tag: 'Home & Power',
    date: 'Sep 10',
    title: 'What Size Inverter Do I Need?',
    dek: 'Running watts times 1.25, then check the surge. That is the easy half. The half that decides whether your plan is buildable is on the DC side, where 3,000 watts at 12 volts means 290 amps of cable, and the answer is a higher bank voltage rather than thicker wire.',
    metaDescription: 'Size an inverter with running watts, surge, and the DC amps nobody mentions. Why 3,000 W at 12 V draws 290 A, and when to build at 24 or 48 V.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'inverter',
      question: 'What voltage is your battery bank?',
      options: ['12 V, small and portable', '24 V, mid-size system', '48 V, house scale', 'All-in-one power station, no idea', 'No battery bank yet'],
      baseSplit: [34, 16, 19, 21, 10],
      startingVotes: 1592,
    },
  },
  {
    slug: 'what-size-mini-split-do-i-need',
    tag: 'Home & Power',
    date: 'Sep 1',
    title: 'What Size Mini Split Do I Need?',
    dek: 'Square footage gets you a bracket, not an answer. Here is the sizing table, the adjustments almost everyone skips, why oversizing is the expensive mistake on an inverter system, and what the thing actually draws when the grid is down.',
    metaDescription: 'Mini split sizing by square footage, the adjustments most people skip, why oversizing costs you, and what one draws on battery or generator power.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'minisplit',
      question: 'What is your backup plan for heating or cooling in an outage?',
      options: ['Mini split on battery or solar', 'Generator running the whole house', 'Wood stove or fuel heater', 'Passive only, seal one room', 'No plan yet'],
      baseSplit: [18, 26, 29, 15, 12],
      startingVotes: 1864,
    },
  },
  {
    slug: 'how-long-does-dry-pasta-last',
    tag: 'Food & Water',
    date: 'Sep 1',
    title: 'How Long Does Dry Pasta Last?',
    dek: 'One to two years in the box, decades sealed, and the date on the side is not a safety date. What actually ends a box of pasta is moisture, the fat in the egg and whole wheat kinds, or insects that were already in the packaging.',
    metaDescription: 'Dry pasta keeps 1 to 2 years boxed and 25 to 30 years sealed. Why the date is not a safety date, and what really ends it: moisture, fat, and pests.',
    authorSlug: 'gordon-nassaur',
  },
  {
    slug: 'how-to-sharpen-a-knife',
    tag: 'Skills',
    date: 'Aug 26',
    title: 'How to Sharpen a Knife',
    dek: 'Nearly everyone owns a sharpener, and nearly everyone still has dull knives. The gap is two things: the angle you are actually holding, and the burr you are not feeling for. Stone, rod, pull-through, or the bottom of a coffee mug, it is the same two things every time.',
    metaDescription: 'Dull knives come down to two things: the angle you are actually holding and the burr you are not feeling for. Stone, rod, or pull-through, same two things.',
    authorSlug: 'arthur-banks',
    vote: {
      key: 'sharpening',
      question: 'How do you sharpen your knives?',
      options: ['Whetstone, freehand', 'Guided system', 'Pull-through sharpener', 'Rod or strop only', 'I take them somewhere'],
      baseSplit: [34, 21, 24, 13, 8],
      startingVotes: 2147,
    },
  },
  {
    slug: 'what-size-backpack-do-i-need',
    tag: 'Gear & Reviews',
    date: 'Aug 26',
    title: 'What Size Backpack Do I Need?',
    dek: 'Liters decide what fits. Torso length decides whether the weight lands on your hips or hangs off your shoulders for three days. And for an emergency bag, one number settles the whole argument: three days of stored water weighs 25 pounds.',
    metaDescription: 'Pack size in liters, matched to how long you are out and what you carry. Torso length, load limits, and why most bug-out bags are bought too large.',
    authorSlug: 'arthur-banks',
    vote: {
      key: 'packsize',
      question: 'What size is your primary emergency bag?',
      options: ['Under 25 L', '25 to 35 L', '35 to 45 L', '45 to 65 L', 'Over 65 L'],
      baseSplit: [9, 26, 33, 22, 10],
      startingVotes: 1592,
    },
  },
  {
    slug: 'how-long-does-a-propane-tank-last',
    tag: 'Home & Power',
    date: 'Aug 19',
    title: 'How Long Does a Propane Tank Last?',
    dek: 'The propane itself never expires, but the cylinder is certified for only 12 years and the runtime is pure arithmetic. A 20 lb tank is 430,000 BTU. Here is how to turn that into hours for any appliance, and why a cold tank starves a burner that has fuel left in it.',
    metaDescription: 'Propane does not expire, but the cylinder is certified for 12 years. Tank sizes, runtime by appliance, the cold weather problem, and safe storage.',
    authorSlug: 'trond-jacobsen',
    vote: {
      key: 'propane',
      question: 'How much propane do you actually keep on hand?',
      options: ['One grill tank', 'Two or three 20 lb tanks', 'A 100 lb cylinder or bigger', 'A permanent home tank', 'None right now'],
      baseSplit: [38, 29, 12, 14, 7],
      startingVotes: 1864,
    },
  },
  {
    slug: 'how-long-does-honey-last',
    tag: 'Food & Water',
    date: 'Aug 19',
    title: 'How Long Does Honey Last?',
    dek: 'Sealed and dry, it does not spoil at all, and the date on the jar is about flavor rather than safety. The chemistry behind that is worth knowing, because it tells you the one thing that actually ruins honey: water.',
    metaDescription: 'Honey does not spoil, but it crystallizes, ferments if wet, and loses quality. What real shelf life looks like, plus the infant safety caveat.',
    authorSlug: 'gordon-nassaur',
  },
  {
    slug: 'how-long-does-gasoline-last',
    tag: 'Home & Power',
    date: 'Aug 11',
    title: 'How Long Does Gas Last? Does Gasoline Go Bad?',
    dek: 'Three months untreated, about a year stabilized, and the clock starts at the pump. What actually goes wrong inside the can, why ethanol makes it worse, and the rotation habit that keeps a generator starting on the second pull.',
    metaDescription: 'Gas lasts about 3 months untreated, 6 ethanol-free, a year stabilized. Signs gas has gone bad, gas can storage, and how to rotate a fuel supply.',
    authorSlug: 'trond-jacobsen',
    vote: {
      key: 'fuel',
      question: 'How old is the gas in your cans right now?',
      options: ['Under 3 months', '3 to 12 months', 'Over a year', 'I genuinely do not know', 'I do not store fuel'],
      baseSplit: [21, 24, 18, 24, 13],
      startingVotes: 2071,
    },
  },
  {
    slug: 'how-long-does-bottled-water-last',
    tag: 'Food & Water',
    date: 'Aug 11',
    title: 'How Long Does Bottled Water Last?',
    dek: 'The FDA does not require that date on the bottle and sets no shelf life for water at all. What the date really means, what actually degrades a stored supply, and the garage habit that quietly ruins it.',
    // 2026-09-22 CTR TEST (run 10): meta description only, title deliberately
    // unchanged so a position shift cannot confound the result. Run 9 pre-registered
    // this: "if coverage grows and clicks do not, the snippet is the next lever."
    // Coverage went 10 -> 13 -> 17 keywords, clicks stayed at 0. Prior copy was:
    // 'Water does not expire, the bottle does. Why plastic is the clock, how long
    //  stored water is really good for, and how to rotate a household supply.'
    metaDescription: 'The FDA sets no shelf life for water and does not require that date. Plan on 2 years for sealed bottles, 6 months for water you filled yourself.',
    authorSlug: 'gordon-nassaur',
  },
  {
    slug: 'how-many-solar-panels-do-i-need',
    tag: 'Home & Power',
    date: 'Aug 05',
    title: 'How Many Solar Panels Do I Need to Power a House?',
    dek: 'Annual kilowatt-hours divided by peak sun hours, derated 14 percent. That is the whole calculation, and it lands between 13 and 25 panels depending almost entirely on where you live. Plus the part sales calls skip: grid-tied solar shuts off in a blackout.',
    metaDescription: 'How many solar panels to power a house: about 20 for the US average, 13 to 25 by sunshine. kW by monthly bill, and what runs in an outage.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'solar',
      question: 'Where are you with home solar?',
      options: ['Installed, no battery', 'Installed with battery backup', 'Portable panels only', 'Getting quotes', 'Priced it and walked away'],
      baseSplit: [17, 14, 27, 19, 23],
      startingVotes: 2418,
    },
  },
  {
    slug: 'what-size-generator-do-i-need',
    tag: 'Home & Power',
    date: 'Jul 28',
    title: 'What Size Generator Do I Need?',
    dek: 'Running watts plus the single biggest starting surge. That is the whole formula, and skipping the surge is why a generator that can run your refrigerator still cannot start it. Worked examples from a fridge on its own up to a whole house.',
    metaDescription: 'Add your running watts, then find the biggest starting surge. Generator sizing by load, fuel tradeoffs, and the carbon monoxide rules that matter.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'gensize',
      question: 'What size generator do you actually own?',
      options: ['Under 3,000 W', '3,000 to 6,500 W', '6,500 to 10,000 W', 'Whole-house standby', 'Battery power station only'],
      baseSplit: [18, 34, 21, 12, 15],
      startingVotes: 1946,
    },
  },
  {
    slug: 'how-to-start-a-fire-without-matches',
    tag: 'Skills',
    date: 'Jul 28',
    title: 'How to Start a Fire Without Matches',
    dek: 'Ferro rod, battery and steel wool, lens, flint and steel, bow drill: what each one is actually good for. Plus the reason most attempts fail, which is never the spark and always the tinder.',
    metaDescription: 'Ferro rod, lens, friction, and battery methods that actually work, plus the tinder and preparation that decide whether any of them light a fire.',
    authorSlug: 'arthur-banks',
    vote: {
      key: 'fire',
      question: 'Your primary backup fire starter is…',
      options: ['Ferro rod', 'Lighter only', 'Stormproof matches', 'Battery and steel wool', 'Flint and steel'],
      baseSplit: [42, 26, 17, 9, 6],
      startingVotes: 3074,
    },
  },
  {
    slug: 'how-to-stay-warm-without-power',
    tag: 'Home & Power',
    date: 'Jul 27',
    title: 'How to Stay Warm Without Power',
    dek: 'Stop trying to heat the house and start heating a person. The room-shrinking, layering, and sleep setup that actually works in a cold house, and the improvised heat sources that kill people every winter.',
    metaDescription: 'Heat the person, then one room, then the house. Layering, sealing a warm room, safe fuel-burning heat, and the carbon monoxide line you do not cross.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'heat',
      question: 'If the grid dropped tonight in winter, your backup heat is…',
      options: ['Wood stove or fireplace', 'Generator running the furnace', 'Rated indoor propane heater', 'Layers and sleeping bags only'],
      baseSplit: [31, 26, 14, 29],
      startingVotes: 2187,
    },
  },
  {
    slug: 'how-long-food-lasts-without-power',
    tag: 'Food & Water',
    date: 'Jul 27',
    title: 'How Long Does Food Last in the Fridge Without Power?',
    dek: 'Four hours in the fridge, 48 in a full freezer, 24 in a half-full one. The USDA numbers that settle the argument, the 40°F rule that decides what gets tossed, and the free prep that doubles your buffer.',
    metaDescription: 'Four hours in a closed fridge, 24 to 48 in a full freezer. The outage clock hour by hour, what to save, what to toss, and how to buy more time.',
    authorSlug: 'trond-jacobsen',
  },
  {
    slug: 'generator-vs-solar',
    tag: 'Home & Power',
    date: 'Jul 12',
    title: 'Generator vs. Solar: What Actually Keeps the Lights On',
    dek: 'Generators are cheap, powerful, and lethal if you place them wrong; solar batteries are silent and fuel-free but weather-limited and pricey. What actually keeps the lights on, from a field engineer who wires both.',
    metaDescription: 'Fuel versus storage, honestly compared: runtime, cost, noise, maintenance, and which one keeps the lights on for the outage you are likely to have.',
    authorSlug: 'cameron-spirz',
    vote: {
      key: 'power',
      question: 'Your primary backup power is…',
      options: ['Dual-fuel generator', 'Solar + battery bank', 'Both, layered'],
      baseSplit: [38, 29, 33],
      startingVotes: 5109,
    },
  },
  {
    slug: 'water-storage-mistake',
    tag: 'Food & Water',
    date: 'Jul 10',
    title: 'The Water Storage Mistake Almost Everyone Makes',
    dek: 'Buying one big water drum and forgetting about it is not a water plan, it is a science experiment. What the CDC, FEMA, and EPA actually say about how much to store, when to rotate, and how to treat water when the tap runs dry.',
    metaDescription: 'Most people store far too little water, and store it wrong. How much you actually need, what containers hold up, and how to keep it drinkable.',
    authorSlug: 'gordon-nassaur',
    vote: {
      key: 'water',
      question: 'How much water do you keep stored?',
      options: ['Under 2 weeks', '2 weeks to a month', 'A month or more'],
      baseSplit: [24, 44, 32],
      startingVotes: 2644,
    },
  },
  {
    slug: 'reading-a-compass',
    tag: 'Skills',
    date: 'Jul 08',
    title: 'Reading a Compass When Your Phone Dies',
    dek: 'Your phone is a great map right up until the battery dies in the cold or the signal vanishes in a canyon. How to navigate home with a $20 compass and a paper map: declination, bearings, and a weekend drill included.',
    metaDescription: 'Taking and following a bearing, handling declination, and navigating by map and compass when your phone is dead. The skill, not the gadget.',
    authorSlug: 'arthur-banks',
  },
  {
    slug: 'bug-out-bag-nobody-needs',
    tag: 'Gear & Reviews',
    date: 'Jul 06',
    title: 'The Bug-Out Bag Nobody Needs (And What to Pack Instead)',
    dek: 'Most bug-out bags fail for one boring reason: they are too heavy to carry on foot. What ultralight backpackers already figured out, and the leaner list to pack instead.',
    metaDescription: 'Most bug-out bags are too heavy to carry and built for the wrong scenario. What to actually pack, what weight does to you, and how far you can go.',
    authorSlug: 'arthur-banks',
    vote: {
      key: 'bob',
      question: 'Honestly, how much does your bug-out bag weigh?',
      options: ['Under 20 lb', '20 to 30 lb', '30 to 40 lb', 'Over 40 lb', 'I have never weighed it'],
      baseSplit: [16, 29, 22, 12, 21],
      startingVotes: 4287,
    },
  },
  {
    slug: 'wound-care-when-help-is-hours-away',
    tag: 'First-Aid',
    date: 'Jul 04',
    title: 'Wound Care When Help Is Hours Away',
    dek: "Pressure, irrigation, and honest limits. What a non-clinician can and can't do responsibly.",
    metaDescription: 'Direct pressure, irrigation, dressing and the infection signs that mean go now. What a non-clinician can and cannot responsibly do when help is hours out.',
    authorSlug: 'gordon-nassaur',
  },
]

export const consensusOfTheWeek: VotePoll = {
  key: 'firstmove',
  question: "When a real warning hits, what's your genuine first move?",
  options: ['Fill every container with water', 'Check fuel & power', 'Call family & confirm the plan', 'Get eyes on local alerts'],
  baseSplit: [41, 22, 19, 18],
  startingVotes: 8730,
}
